import React from 'react'

export interface CommunityTabItem<K extends string = string> {
  key: K
  label: string
  /**
   * Count chip state:
   * - number   → rendered as the visible count
   * - null     → rendered as an invisible placeholder that reserves layout
   *              width (for pending-data states — keeps tab dimensions
   *              stable between "data-pending" and "data-loaded").
   * - omitted  → no count chip at all (for tabs that have no count
   *              concept, like Discover).
   */
  count?: number | null
}

export interface CommunityTabBarProps<K extends string = string> {
  tabs: CommunityTabItem<K>[]
  activeTab: K
  onTabChange: (key: K) => void
  /** Extra class applied to the root element — use for context-specific margins */
  className?: string
}

function CommunityTabBar<K extends string> ({
  tabs,
  activeTab,
  onTabChange,
  className,
}: CommunityTabBarProps<K>) {
  const rootClass = className
    ? `community-tab-bar ${className}`
    : 'community-tab-bar'

  return (
    <div className={rootClass} role="tablist">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`community-tab-bar__tab${isActive ? ' community-tab-bar__tab--active' : ''}`}
            onClick={() => onTabChange(tab.key)}
          >
            <span className="community-tab-bar__tab-label">{tab.label}</span>
            {/* Three-state count chip, driven by the `count` field:
                - number  → render the visible count
                - null    → render an invisible placeholder (non-breaking
                            space + --placeholder modifier). Reserves the
                            slot's width (desktop) and stacked row height
                            (mobile) so tab dimensions stay stable between
                            "data-pending" and "data-loaded".
                - omitted → render nothing at all, for tabs with no count
                            concept (e.g., Discover). */}
            {tab.count !== undefined ? (
              <span
                className={`community-tab-bar__tab-count${tab.count === null ? ' community-tab-bar__tab-count--placeholder' : ''}`}
                aria-hidden={tab.count === null ? true : undefined}
              >
                {tab.count !== null ? tab.count : '\u00A0'}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export default CommunityTabBar
